/**
 * The promotions pages' scope — `?market=&from=&to=` (and `?window=` on the window's page, and
 * `?store=` on one store's page of the window).
 *
 * CLAUDE.md §3.4, the house pattern for a parameter-bounded read, with no exception taken:
 *
 *   - **No default, ever.** Not "the last 13 weeks", not "the pilot market". A range chosen for
 *     the planner is the unbounded read with an extra step (G16), and the windows a page shows
 *     depend entirely on it. The quick picks WRITE a range to the link when pressed; nothing is
 *     written by itself. The market is the planner's too (DEC-85: no market is defaulted).
 *   - **Parse from a string, not from `window`** — `readPromotionScope(search)` is pure, so the
 *     export resolvers read the same scope from the `ExportScope` they are handed.
 *   - A malformed date is absent, and so is a range whose end is before its start: the page asks
 *     for the period again rather than sending a typo.
 */

/** The market — sent as `country_key` to both reads. */
export const MARKET_PARAM = 'market';
/** The range's first and last day, both included (ISO `YYYY-MM-DD`). */
export const FROM_PARAM = 'from';
export const TO_PARAM = 'to';
/** The window a drill opened — the window page's own key. */
export const WINDOW_PARAM = 'window';
/** The store a window's store list opened — one store's sub-categories (`docs/adr/8006`). */
export const STORE_PARAM = 'store';

export interface PromotionPeriod {
  readonly from: string;
  readonly to: string;
}

export interface PromotionScope {
  /** `null`: no market chosen. It does NOT mean "every market". */
  readonly market: string | null;
  /** `null`: no period chosen, or one that cannot be read. */
  readonly period: PromotionPeriod | null;
  readonly windowId: string | null;
}

/** A real calendar day written `YYYY-MM-DD`. Pure. */
export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

const param = (params: URLSearchParams, key: string): string | null => {
  const value = params.get(key)?.trim() ?? '';
  return value === '' ? null : value;
};

/** The scope a search string states. Pure: a string in, never `window`, no default. */
export function readPromotionScope(search: string): PromotionScope {
  const params = new URLSearchParams(search);
  const from = param(params, FROM_PARAM);
  const to = param(params, TO_PARAM);
  const period = from !== null && to !== null && isCalendarDate(from) && isCalendarDate(to) && from <= to ? { from, to } : null;
  return { market: param(params, MARKET_PARAM), period, windowId: param(params, WINDOW_PARAM) };
}

/**
 * The store on one store's page of a window. Its own parser, not a field of
 * {@link readPromotionScope}: only that page reads it (the URL-parameter census, ADR 9054). `null`:
 * no store on the link — never "the first store". Pure.
 */
export function readPromotionStore(search: string): string | null {
  return param(new URLSearchParams(search), STORE_PARAM);
}

/** The quick picks, in weeks: a quarter, half a year, a year. */
export const QUICK_PERIOD_WEEKS = [13, 26, 52] as const;

/**
 * The last `weeks` weeks, today included, in UTC — the calendar the tills' days are in. `now` is
 * an argument so the test fixes the clock. Pure.
 */
export function lastWeeks(weeks: number, now: number): PromotionPeriod {
  const today = new Date(now);
  const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - (weeks * 7 - 1)));
  return { from: start.toISOString().slice(0, 10), to: today.toISOString().slice(0, 10) };
}

/** The range's length in calendar days, both ends included. Pure. */
export function periodDays(period: PromotionPeriod): number {
  return Math.round((Date.parse(`${period.to}T00:00:00Z`) - Date.parse(`${period.from}T00:00:00Z`)) / 86_400_000) + 1;
}

/** `search` with the given keys set (or removed, for `null`), every other key kept. Pure. */
export function withScope(search: string, values: Readonly<Record<string, string | null>>): string {
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(values)) {
    if (value === null) params.delete(key);
    else params.set(key, value);
  }
  const next = params.toString();
  return next === '' ? '' : `?${next}`;
}

/** The pages' paths — one place, so a link and the manifest cannot drift apart. */
export const PROMOTIONS_PATH = '/postseason/promotions';
export const PROMOTION_WINDOW_PATH = '/postseason/promotions/window';
export const PROMOTION_MECHANICS_PATH = '/postseason/promotions/mechanics';
/** A window's reference perimeter, one page per list (bata-platform ADR 8016, `docs/adr/8005`). */
export const PROMOTION_WINDOW_STORES_PATH = '/postseason/promotions/window/stores';
export const PROMOTION_WINDOW_ARTICLES_PATH = '/postseason/promotions/window/articles';
/** One store's sub-categories in the window (bata-platform ADR 8017, `docs/adr/8006`). */
export const PROMOTION_WINDOW_STORE_PATH = '/postseason/promotions/window/store';

/** A window's page: the scope the timeline was read under, and the window. Pure. */
export function windowHref(search: string, windowId: string): string {
  return `${PROMOTION_WINDOW_PATH}${withScope(search, { [WINDOW_PARAM]: windowId })}`;
}

/** A window's stores or articles: the window page's own scope, carried as it is. Pure. */
export function perimeterHref(path: string, search: string): string {
  return `${path}${withScope(search, {})}`;
}

/** One store of the window's store list: the list's own scope, and the store. Pure. */
export function storeHref(search: string, store: string): string {
  return `${PROMOTION_WINDOW_STORE_PATH}${withScope(search, { [STORE_PARAM]: store })}`;
}
